import { useState, useRef, useEffect, useContext } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";

import MainButton from "../MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import LangCurrencyModal from "../LangCurrencyModal/LangCurrencyModal";
import LogoIcon from "../../../assets/icons/logo.png";
import cartBlue from "../../../assets/icons/cart/cart-primary.png";
import cartGray from "../../../assets/icons/cart/cart-gray.png";
import arrowImg from "../../../assets/icons/data/sort_down.png";
import arrowDown from "../../../assets/icons/data/sort_down.png";

import "./Header.css";
import { SUPPORTED_LANGUAGES } from "../../../i18n/i18n";

const NavigationHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { token, setToken, role, walletCurrency, changeCurrency } = useContext(AuthContext);
  const { itemCount } = useCart();

  const [isMounted, setIsMounted] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [isPrefModalOpen, setIsPrefModalOpen] = useState(false);
  const [prefInitialTab, setPrefInitialTab] = useState("language");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const profileRef = useRef(null);
  const adminRef = useRef(null);
  const langCurrencyRef = useRef(null);

  const currentLangCode = i18n.language || "en";

  const handleSignOut = () => {
    setToken(null);
    localStorage.removeItem("token");
    navigate(`/${currentLangCode}/sign-in`);
  };

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    const pathname = location.pathname;
    if (pathname.startsWith("/admin") || pathname === "/config" || pathname === "/partner-offer") {
      return;
    }
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length > 0 && SUPPORTED_LANGUAGES.includes(parts[0])) {
      parts[0] = newLang;
    } else {
      parts.unshift(newLang);
    }
    const newPath = "/" + parts.join("/");
    navigate(newPath + location.search + location.hash);
  };

  const handleCurrencySelect = async (newCurr) => {
    if (changeCurrency) {
      await changeCurrency(newCurr);
    }
    setIsPrefModalOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (adminRef.current && !adminRef.current.contains(e.target)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick, true);
    return () => {
      document.removeEventListener("mousedown", handleClick, true);
    };
  }, []);

  return (
    <>
      <nav className="header">
        <div className="header-left">
          <button className="hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <span />
            <span />
            <span />
          </button>

          <div className="header-logo" onClick={() => navigate(`/${currentLangCode}/shop`)}>
            <img src={LogoIcon} alt="Logo" className="logo-icon" />
            <span className="header-title">Telviora</span>
          </div>
        </div>

        <div className="header-links">
          <NavLink to={`/${currentLangCode}`} className="nav-item">
            {t("nav.home")}
          </NavLink>
          <NavLink to={`/${currentLangCode}/shop`} className="nav-item">
            {t("nav.buyEsim")}
          </NavLink>

          <NavLink to={`/${currentLangCode}/faq`} className="nav-item">
            {t("nav.faq")}
          </NavLink>

          <NavLink to={`/${currentLangCode}/contact`} className="nav-item">
            {t("nav.contact")}
          </NavLink>

          {token && (
            <div className="header-dropdown" ref={profileRef}>
              <button onClick={() => setProfileOpen((prev) => !prev)}>
                {t("nav.account")}{" "}
                <img src={arrowImg} alt="" className={`header-dropdown-arrow ${profileOpen ? "open" : ""}`} />
              </button>
              {profileOpen && (
                <div className="dropdown-menu">
                  <NavLink to={`/${currentLangCode}/account`} onClick={() => setProfileOpen(false)}>
                    {t("nav.account")}
                  </NavLink>
                  <NavLink to={`/${currentLangCode}/affiliation`} onClick={() => setProfileOpen(false)}>
                    {t("nav.affiliation")}
                  </NavLink>
                </div>
              )}
            </div>
          )}

          {token && (
            <NavLink to={`/${currentLangCode}/esim`} className="nav-item">
              {t("nav.esim")}
            </NavLink>
          )}

          {token && role?.name === "admin" && (
            <div className="header-dropdown" ref={adminRef}>
              <button onClick={() => setAdminOpen((v) => !v)}>
                <span>{t("nav.admin")}</span>
                <img src={arrowDown} alt="" className={`header-dropdown-arrow ${adminOpen ? "open" : ""}`} />
              </button>
              {adminOpen && (
                <div className="dropdown-menu">
                  <NavLink to={`/admin/users`} onClick={() => setAdminOpen(false)}>
                    {t("nav.users")}
                  </NavLink>
                  <NavLink to={`/admin/wallets`} onClick={() => setAdminOpen(false)}>
                    {t("nav.wallets")}
                  </NavLink>
                  <NavLink to={`/admin/transactions`} onClick={() => setAdminOpen(false)}>
                    {t("nav.transactions")}
                  </NavLink>
                  <NavLink to={`/admin/promocodes`} onClick={() => setAdminOpen(false)}>
                    {t("nav.promocodes")}
                  </NavLink>
                  <NavLink to={`/admin/notifications`} onClick={() => setAdminOpen(false)}>
                    {t("nav.notifications")}
                  </NavLink>
                  <NavLink to={`/admin/support-chats`} onClick={() => setAdminOpen(false)}>
                    {t("nav.supportChats")}
                  </NavLink>
                  <NavLink to={`/partner-offer`} onClick={() => setAdminOpen(false)}>
                    {t("nav.partnerOffer")}
                  </NavLink>
                  <NavLink to={`/admin/esim`} onClick={() => setAdminOpen(false)}>
                    {t("nav.esimStats")}
                  </NavLink>
                  <NavLink to={`/config`} onClick={() => setAdminOpen(false)}>
                    {t("nav.config")}
                  </NavLink>
                </div>
              )}
            </div>
          )}

          <NavLink
            to={`/${currentLangCode}/cart`}
            className="header-cart-link"
            aria-label="Open cart"
            title="Open cart"
          >
            <span className="header-cart-icon-wrap">
              <img
                src={isMounted && itemCount > 0 ? cartBlue : cartGray}
                alt=""
                className="header-cart-icon"
                aria-hidden="true"
              />
              {isMounted && itemCount > 0 && (
                <span className="header-cart-badge" aria-hidden="true">
                  {itemCount}
                </span>
              )}
            </span>
          </NavLink>

          <div className="header-actions">
            {token ? (
              <MainButton className="btn btn-primary" onClick={handleSignOut}>
                {t("nav.signOut")}
              </MainButton>
            ) : (
              <>
                <SecondButton
                  onClick={() =>
                    navigate(`/${currentLangCode}/register`, {
                      state: { resetForgotPassword: true },
                    })
                  }
                >
                  {t("nav.register")}
                </SecondButton>
                <MainButton
                  className="btn btn-primary"
                  onClick={() =>
                    navigate(`/${currentLangCode}/sign-in`, {
                      state: { resetForgotPassword: true },
                    })
                  }
                >
                  {t("nav.signIn")}
                </MainButton>
              </>
            )}

            <div className="header-lang-currency-wrap" ref={langCurrencyRef}>
              <button
                type="button"
                className="header-lang-currency-btn"
                onClick={() => {
                  setPrefInitialTab("language");
                  setIsPrefModalOpen(true);
                }}
                title={t("header.preferences", "Preferences")}
              >
                <span className="header-lang-currency-bottom">
                  {`${currentLangCode.toUpperCase()} | ${isMounted ? walletCurrency : "EUR"}`}
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <LangCurrencyModal
        isOpen={isPrefModalOpen}
        onClose={() => setIsPrefModalOpen(false)}
        currentLang={currentLangCode}
        currentCurrency={walletCurrency || "EUR"}
        initialTab={prefInitialTab}
        onSelectLanguage={(newLang) => {
          handleLanguageChange(newLang);
          setIsPrefModalOpen(false);
        }}
        onSelectCurrency={handleCurrencySelect}
      />

      {/* Sidenav Mobile */}
      {mobileOpen && <div className="menu-overlay" onClick={() => setMobileOpen(false)} aria-hidden="true" />}
      <div className={`sidenav ${mobileOpen ? "open" : ""}`}>
        <div className="sidenav-header">
          <button className="close-btn" onClick={() => setMobileOpen(false)}>
            &times;
          </button>

          <button
            type="button"
            className="header-lang-currency-btn"
            onClick={() => {
              setPrefInitialTab("language");
              setIsPrefModalOpen(true);
            }}
          >
            <span className="header-lang-currency-bottom">
              {`${currentLangCode.toUpperCase()} | ${walletCurrency || "EUR"}`}
            </span>
          </button>
        </div>

        <NavLink to={`/${currentLangCode}`} onClick={() => setMobileOpen(false)}>
          {t("nav.home")}
        </NavLink>

        <NavLink to={`/${currentLangCode}/shop`} onClick={() => setMobileOpen(false)}>
          {t("nav.buyEsim")}
        </NavLink>

        {token && (
          <>
            <NavLink to={`/${currentLangCode}/account`} onClick={() => setMobileOpen(false)}>
              {t("nav.account")}
            </NavLink>
            <NavLink to={`/${currentLangCode}/affiliation`} onClick={() => setMobileOpen(false)}>
              {t("nav.affiliation")}
            </NavLink>
            <NavLink to={`/${currentLangCode}/esim`} onClick={() => setMobileOpen(false)}>
              {t("nav.esim")}
            </NavLink>
          </>
        )}

        <NavLink to={`/${currentLangCode}/contact`} onClick={() => setMobileOpen(false)}>
          {t("nav.contact")}
        </NavLink>

        <NavLink to={`/${currentLangCode}/faq`} onClick={() => setMobileOpen(false)}>
          {t("nav.faq")}
        </NavLink>

        <NavLink
          to={`/${currentLangCode}/cart`}
          onClick={() => setMobileOpen(false)}
          className="header-cart-link"
          aria-label="Open cart"
          title="Open cart"
        >
          <span className="header-cart-icon-wrap">
            <img
              src={isMounted && itemCount > 0 ? cartBlue : cartGray}
              alt=""
              className="header-cart-icon"
              aria-hidden="true"
            />
            {isMounted && itemCount > 0 && (
              <span className="header-cart-badge" aria-hidden="true">
                {itemCount}
              </span>
            )}
          </span>
        </NavLink>

        {token && role?.name === "admin" && (
          <div className="sidenav-admin-section">
            <NavLink to="/admin/users" onClick={() => setMobileOpen(false)}>
              {t("nav.users")}
            </NavLink>
            <NavLink to="/admin/wallets" onClick={() => setMobileOpen(false)}>
              {t("nav.wallets")}
            </NavLink>
            <NavLink to="/admin/transactions" onClick={() => setMobileOpen(false)}>
              {t("nav.transactions")}
            </NavLink>
            <NavLink to="/admin/promocodes" onClick={() => setMobileOpen(false)}>
              {t("nav.promocodes")}
            </NavLink>
            <NavLink to="/admin/notifications" onClick={() => setMobileOpen(false)}>
              {t("nav.notifications")}
            </NavLink>
            <NavLink to="/admin/support-chats" onClick={() => setMobileOpen(false)}>
              {t("nav.supportChats")}
            </NavLink>
            <NavLink to="/partner-offer" onClick={() => setMobileOpen(false)}>
              {t("nav.partnerOffer")}
            </NavLink>
            <NavLink to="/admin/esim" onClick={() => setMobileOpen(false)}>
              {t("nav.esimStats")}
            </NavLink>
            <NavLink to="/config" onClick={() => setMobileOpen(false)}>
              {t("nav.config")}
            </NavLink>
          </div>
        )}

        {token ? (
          <MainButton className="btn btn-primary sidenav-btn" onClick={handleSignOut}>
            {t("nav.signOut")}
          </MainButton>
        ) : (
          <div className="sidenav-auth-buttons">
            <div
              onClick={() => {
                setMobileOpen(false);
                navigate(`/${currentLangCode}/register`, {
                  state: { resetForgotPassword: true },
                });
              }}
              className="sidenav-btn-link"
            >
              <MainButton className="btn btn-primary sidenav-btn">{t("nav.register")}</MainButton>
            </div>
            <div
              onClick={() => {
                setMobileOpen(false);
                navigate(`/${currentLangCode}/sign-in`, {
                  state: { resetForgotPassword: true },
                });
              }}
              className="sidenav-btn-link"
            >
              <MainButton className="btn btn-primary sidenav-btn">{t("nav.signIn")}</MainButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NavigationHeader;
